/**
 * `@bind` parser for the proof. grammar (Track 2.A / P3.GRAMMAR).
 *
 * Parses: `@bind variableName = expression, pivot: pivotName`
 *
 * From extensibility-design.md §3.4.
 *
 * MVP: single pivot only. Multi-pivot constraint satisfaction is Phase 2.
 * Back-solve / symbolic inversion: wishlisted — not implemented here.
 * `@scale` parsing: wishlisted — not implemented here.
 *
 * L13 (spaced identifiers): `isValidIdent` accepts internal single spaces
 * per extensibility-design.md §3.4.0.
 *
 * L11 (labelled-line equation auto-detection): the exported
 * `isLabelledLineEquation` helper is used by the parser (index.ts) to
 * auto-promote a labelled line to a BindingNode when its RHS parses as
 * a valid expression.
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import type { BindingNode, ScopePath } from "../ast/types.js";
import { parseExpression, validateIdentifier } from "./expressions.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface BindingParseResult {
  node: BindingNode | null;
  errors: string[];
}

/**
 * Parse the args portion of an `@bind` line.
 *
 * Input examples (after stripping `@bind `):
 *   `hydration = water / flour * 100, pivot: water`
 *   `salt_pct = salt / flour * 100, pivot: salt`
 *   `total = sum(@ingredients.mass), pivot: proportional`
 *   `Tipo 00 Flour pct = Tipo 00 Flour / total flour * 100, pivot: Tipo 00 Flour`
 */
export function parseBinding(argsStr: string, scopePath: ScopePath): BindingParseResult {
  const errors: string[] = [];
  const trimmed = argsStr.trim();

  // Split on `, pivot:` (required separator)
  const pivotIdx = trimmed.lastIndexOf(", pivot:");
  if (pivotIdx < 0) {
    errors.push(`@bind: missing 'pivot:' clause. Expected form: @bind name = expr, pivot: varName`);
    return { node: null, errors };
  }

  const bindingPart = trimmed.slice(0, pivotIdx).trim();
  const pivotPart = trimmed.slice(pivotIdx + ", pivot:".length).trim();

  // Split binding part on first `=`
  const eqIdx = bindingPart.indexOf("=");
  if (eqIdx < 0) {
    errors.push(`@bind: missing '=' in binding. Expected form: @bind name = expr, pivot: varName`);
    return { node: null, errors };
  }

  const variableName = bindingPart.slice(0, eqIdx).trim();
  const exprStr = bindingPart.slice(eqIdx + 1).trim();

  // Validate identifier per L13 rules (allows internal spaces)
  const identError = validateIdentifier(variableName);
  if (identError !== null) {
    const msg = identErrorMessage(identError, variableName);
    errors.push(`@bind: ${msg}`);
    return { node: null, errors };
  }

  // Parse the expression
  const exprResult = parseExpression("=" + exprStr);
  if (exprResult.node === null) {
    errors.push(...exprResult.errors.map((e) => `@bind expression: ${e}`));
    return { node: null, errors };
  }

  const pivot = pivotPart;
  const pivotError = validateIdentifier(pivot);
  if (pivot !== "proportional" && pivotError !== null) {
    errors.push(
      `@bind: invalid pivot '${pivot}'. Must be a variable name or 'proportional'. ${identErrorMessage(pivotError, pivot)}`,
    );
    return { node: null, errors };
  }

  const node: BindingNode = {
    kind: "binding",
    variableName,
    expression: exprResult.node,
    pivot,
    scopePath,
  };

  return { node, errors };
}

// ---------------------------------------------------------------------------
// Labelled-line equation auto-detection (L11)
// ---------------------------------------------------------------------------

/**
 * Determine whether the RHS of a labelled line should be treated as an
 * expression (equation) rather than a plain literal value.
 *
 * A line is an equation when its RHS:
 *   - Starts with `=` (explicit form), or
 *   - Contains at least one operator AND parses as a valid expression
 *     without errors (implicit inferred-binding form per L11).
 *
 * The labelled-line context parser calls this helper; when it returns true,
 * the parser promotes the line to an expression node and auto-creates a
 * binding or expression-kind labelled line.
 *
 * Returns the parsed ExpressionNode on success, null if it should remain
 * a literal.
 *
 * Note: Back-solve / `@scale` are wishlisted — NOT implemented here.
 */
export function tryParseAsExpression(rhs: string): import("../ast/types.js").ExpressionNode | null {
  const trimmed = rhs.trim();

  // Explicit = prefix → always an expression
  if (trimmed.startsWith("=")) {
    const result = parseExpression(trimmed);
    return result.errors.length === 0 ? result.node : null;
  }

  // Implicit inference: does the RHS contain an operator that makes it look
  // like an expression? Quick structural check to avoid expensive parsing
  // of every plain literal (e.g. "400g").
  // We look for: operators (+, -, *, /, ^) or reference forms
  // (another identifier after a space, @section.field, scope/path).
  // Simple heuristic: skip if it's a bare number-with-optional-unit.
  if (/^\d+(?:\.\d+)?(?:[a-zA-Z%°][a-zA-Z0-9%°]*)?$/.test(trimmed)) {
    // Looks like a plain literal — no expression
    return null;
  }

  // Try to parse. Succeed only if no errors and the node is non-trivial
  // (not just a LiteralNode — that would be a plain value, not an equation).
  const result = parseExpression("=" + trimmed);
  if (result.errors.length > 0 || result.node === null) return null;
  if (result.node.kind === "literal") return null;

  return result.node;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Per the L13 identifier rule (extensibility-design.md §3.4.0):
 * A valid identifier:
 *   - Is non-empty after trim.
 *   - Contains only A-Za-z0-9, ASCII space (internal only), hyphens (mid-only), underscores.
 *   - Does not start or end with a space.
 *   - Is not a reserved keyword.
 *
 * This relaxes the prior `[a-zA-Z_][a-zA-Z0-9_-]*` regex to allow internal spaces.
 */
export function isValidIdent(name: string): boolean {
  return validateIdentifier(name) === null;
}

function identErrorMessage(
  code: "E_IDENT_EMPTY" | "E_IDENT_LEADING_SPACE" | "E_IDENT_TRAILING_SPACE" | "E_RESERVED_KEYWORD",
  name: string,
): string {
  switch (code) {
    case "E_IDENT_EMPTY":
      return "Empty variable name. Check for a stray colon or operator.";
    case "E_IDENT_LEADING_SPACE":
      return `Variable names must not start with a space. Trimmed to "${name.trimStart()}".`;
    case "E_IDENT_TRAILING_SPACE":
      return `Variable names must not end with a space. Trimmed to "${name.trimEnd()}".`;
    case "E_RESERVED_KEYWORD":
      return `"${name}" is a reserved keyword and cannot be used as a variable name.`;
  }
}
